<template>
  <div>
    <v-card-title>
      <v-row>
        <v-col cols="12" sm="5" md="3">
          <v-text-field v-model="search" append-icon="search" :label="$t('global.search')" single-line hide-details />
        </v-col>
        <v-spacer />
        <v-col cols="12" sm="6" md="3">
          <v-select
            multiple
            v-model="selectedColumns"
            append-icon="fa-columns"
            :label="$t('global.columns')"
            single-line
            hide-details
            small-chips
            :items="availableColumns"
            item-text="text"
            item-value="selectValue"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip v-if="index < 2" color="primary" text-color="white" small>
                <span>{{ item.text }}</span>
              </v-chip>
              <span v-if="index === 2" class="grey--text caption">(+{{ selectedColumns.length - 2 }} {{ $t("global.others") }})</span>
            </template>
          </v-select>
        </v-col>
      </v-row>
    </v-card-title>

    <v-data-table
      :custom-filter="filterOverAllProperty"
      :headers="headers"
      :items="bookings"
      :sort-by.sync="pagination.sortBy"
      :sort-desc.sync="pagination.descending"
      :search="search"
      :loading="$store.getters['Ajax/loading'] ? 'accent' : false"
      :items-per-page="5"
    >
      <template #item="props">
        <tr>
          <td v-for="column of selectedColumns" :key="column">
            <template v-if="column === 'creation_date'">
              {{ formatDateView(props.item.creation_date) }}
            </template>
            <template v-if="column === 'reservation_number'">
              {{ props.item.reservation_number }}
              <v-chip label outlined small style="color: #FFE3F3; background-color: #ff99d0 !important;" v-if="props.item.is_cancelled === 1"
                >{{ $t("booking.canceled") }}
              </v-chip>
              <assigned-delegations-chip :delegations="props.item.delegations" />
            </template>
            <template v-if="column === 'route'">
              <div v-if="props.item.flightAttributes.start_airport_code == null">
                {{ props.item.flightAttributes.itinerary }}
                <div class="flight-date grey--text">
                  {{ $t("booking.flight.missingDetailedInformation") }}
                </div>
              </div>
              <div v-else>
                <v-row>
                  <v-col class="flight-part">
                    <div class="flight-direction grey--text">
                      {{ $t("booking.flight.departure") }}
                    </div>
                    <div class="flight-airport">
                      <span class="bigger" style="font-weight: bold">
                        <v-tooltip bottom color="white">
                          <template #activator="{on}">
                            <span class="accent--text" v-on="on">{{ props.item.flightAttributes.start_airport_code }}</span>
                          </template>
                          <span style="color: #0f0f0f">{{ props.item.flightAttributes.start_airport_name }}</span>
                        </v-tooltip>
                        <span class="arrow">➞</span>
                        <v-tooltip bottom color="white">
                          <template #activator="{on}">
                            <span class="accent--text" v-on="on">{{ props.item.flightAttributes.end_airport_code }}</span>
                          </template>
                          <span style="color: #0f0f0f">{{ props.item.flightAttributes.end_airport_name }}</span>
                        </v-tooltip>
                      </span>
                      {{ props.item.flightAttributes.start_time != null ? props.item.flightAttributes.start_time.substr(0, 5) : $t("global.noData") }}
                      -
                      {{ props.item.flightAttributes.end_time != null ? props.item.flightAttributes.end_time.substr(0, 5) : $t("global.noData") }}
                    </div>
                    <div class="flight-date grey--text">
                      {{
                        props.item.flightAttributes.start_date != null ? dayOfWeek(props.item.flightAttributes.start_date.substr(0, 10)) : $t("global.noData")
                      }}
                      {{ props.item.flightAttributes.start_date != null ? formatDateView(props.item.flightAttributes.start_date) : $t("global.noData") }}
                    </div>
                  </v-col>
                  <v-col class="flight-part" v-if="props.item.flightAttributes.return_start_airport_code != null">
                    <div class="flight-direction grey--text">
                      {{ $t("booking.flight.arrival") }}
                    </div>
                    <div class="flight-airport">
                      <span class="bigger" style="font-weight: bold">
                        <v-tooltip bottom color="white">
                          <template #activator="{on}">
                            <span class="accent--text" v-on="on">{{ props.item.flightAttributes.return_start_airport_code }}</span>
                          </template>
                          <span style="color: #0f0f0f">{{ props.item.flightAttributes.return_start_airport_name }}</span>
                        </v-tooltip>
                        <span class="arrow">➞</span>
                        <v-tooltip bottom color="white">
                          <template #activator="{on}">
                            <span class="accent--text" v-on="on">{{ props.item.flightAttributes.return_end_airport_code }}</span>
                          </template>
                          <span style="color: #0f0f0f">{{ props.item.flightAttributes.return_end_airport_name }}</span>
                        </v-tooltip>
                      </span>
                      {{ props.item.flightAttributes.return_start_time.substr(0, 5) }}
                      -
                      {{ props.item.flightAttributes.return_end_time.substr(0, 5) }}
                    </div>
                    <div class="flight-date grey--text">
                      {{ dayOfWeek(props.item.flightAttributes.return_start_date.substr(0, 10)) }}
                      {{ formatDateView(props.item.flightAttributes.return_start_date) }}
                    </div>
                  </v-col>
                </v-row>
              </div>
            </template>
            <template v-if="column === 'organization_unit'">
              {{ props.item.organization_unit }}
            </template>
            <template v-if="column === 'ticket_number'">
              {{ props.item.flightAttributes.ticket_number }}
            </template>
            <template v-if="column === 'passengers'">
              <!-- eslint-disable-next-line -->
              <span v-for="passenger of props.item.passengers" style="text-transform: capitalize"> {{ passenger }} <br /> </span>
            </template>
            <template v-if="column === 'total_amount'">
              <span class="datatable-total-amount">
                <template v-if="props.item.invoice_gross_value !== null">{{ props.item.invoice_gross_value }} {{ props.item.invoice_currency }}</template>
                <template v-else-if="props.item.total_amount !== null">{{ props.item.total_amount }} PLN</template>
              </span>
            </template>
          </td>
          <td class="text-center text-no-wrap">
            <slot name="action-column" :booking="props.item">
              <v-tooltip bottom>
                {{ $t("global.goToDetails") }}
                <template #activator="{on}">
                  <v-btn :to="'/booking/flight/detail/' + props.item.id" class="mx-0" icon v-on="on">
                    <v-icon>remove_red_eye</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>
              <v-tooltip bottom>
                {{ $t("global.downloadFile") }}
                <template #activator="{on}">
                  <v-btn :disabled="$store.getters['Ajax/loading']" @click="downloadAttachment(props.item.id)" class="mx-0" icon v-on="on">
                    <v-icon>cloud_download</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>
            </slot>
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import dayOfWeek from "../../repository/shortDayOfWeekFromDate";
import filterOverAllProperty from "../../repository/filterOverAllProperty";
import formatDateView from "../../repository/fotmatDateView";
import downloadFile from "../../repository/downloadFile";
import AssignedDelegationsChip from "./AssignedDelegationsChip";

export default {
  name: "BookingFlightDataTable",
  components: { AssignedDelegationsChip },
  data: () => ({
    //headers: [], // moved to "created" due to use translations
    pagination: {
      sortBy: "creation_date",
      descending: true
    },
    bookings: [],
    search: null,
    selectedColumns: ["creation_date", "reservation_number", "passengers", "total_amount"]
  }),
  computed: {
    isWNBUser() {
      return this.$store.getters["isWnbUser"];
    },
    availableColumns() {
      return [
        { text: this.$t("booking.dateShort"), value: "creation_date", selectValue: "creation_date" },
        { text: this.$t("booking.numberShort"), value: "reservation_number", selectValue: "reservation_number" },
        { text: this.$t("booking.flight.route"), value: "", sortable: false, selectValue: "route" },
        { text: this.$t("booking.ticketNumber"), value: "flightAttributes.ticket_number", selectValue: "ticket_number" },
        { text: this.$t("booking.passengers"), sortable: false, selectValue: "passengers" },
        { text: this.$t("booking.organizationUnit"), value: "organization_unit", selectValue: "organization_unit" },
        { text: this.$t("booking.price"), value: "total_amount", selectValue: "total_amount" }
        // { text: "", value: "actions", sortable: false, selectValue: "" }
      ];
    },
    headers() {
      let headers = [];
      for (let selectedColumn of this.selectedColumns) {
        headers.push(this.availableColumns.find(v => v.selectValue === selectedColumn));
      }
      headers.push({ text: "", value: "actions", sortable: false });
      return headers;
    }
  },
  methods: {
    dayOfWeek: dayOfWeek,
    filterOverAllProperty: filterOverAllProperty,
    formatDateView: formatDateView,
    downloadAttachment(id) {
      downloadFile("booking/get-booking-file?bookingId=" + id);
    },
    downloadFile: downloadFile
  },
  created() {
    this.axios.get("booking/get-flight").then(resp => {
      this.bookings = resp.data;
    });
  }
};
</script>

<style scoped>
.chip--small {
  height: 22px;
}

.flight-direction {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.grey--text {
  color: #9e9e9e !important;
}

.bigger {
  margin-right: 4px;
  font-size: 15px;
}

.flight-airport {
  font-weight: 700;
  line-height: 1;
}

.flight-part {
  min-width: 175px;
}
</style>
